/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSynced,
  DataSyncFailed,
  DataSyncing,
  EnablePolicyRadioGroup,
  ProvisionOrgInfo,
  StepIcon,
} from './share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import {
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  MultiAppsAuthenticationInfoRes,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '@Types/provision';
import { ProvisionApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import RejectProvisionModal from './rejectProvisionModal';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';
import { App, Service } from '@Types/app';

enum SharePointGraphProvisionStatus {
  NotProvision = 0,
  ProvisioningGrantStep1Done = 1,
  ProvisioningGrantStep2Done = 2,
  Provisioned = 3,
}

const SERVICE = Service.SharePoint;

const SharePointGraphModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const { t } = useTranslation('provision');
  const [errorMsg, setErrorMsg] = useState<ReactNode | undefined>(undefined);
  const getInitStatus = () => {
    if (provisionStatus.status === ProvisionStatusEnum.NotStarted) {
      return SharePointGraphProvisionStatus.NotProvision;
    } else if (provisionStatus.status === ProvisionStatusEnum.Provisioned) {
      return SharePointGraphProvisionStatus.Provisioned;
    } else {
      const { apps: appInfo } = provisionStatus?.authorization as {
        apps: {
          app: string;
          status: string;
        }[];
      };
      // first app is sharepoint, second app is sharepointBasic
      const [sharepointApp, sharepointBasicApp] = appInfo;
      if (provisionStatus.status === ProvisionStatusEnum.Provisioning) {
        if (
          sharepointApp.status === TokenStatus.NotStarted ||
          sharepointApp.status === TokenStatus.Created
        ) {
          return SharePointGraphProvisionStatus.ProvisioningGrantStep1Done;
        } else {
          return SharePointGraphProvisionStatus.ProvisioningGrantStep2Done;
        }
      } else {
        const statusArr = [
          TokenStatus.NotStarted,
          TokenStatus.Failure,
        ] as string[];
        if (statusArr.includes(sharepointApp.status)) {
          return SharePointGraphProvisionStatus.NotProvision;
        } else if (statusArr.includes(sharepointBasicApp.status)) {
          return SharePointGraphProvisionStatus.ProvisioningGrantStep1Done;
        } else {
          return SharePointGraphProvisionStatus.ProvisioningGrantStep2Done;
        }
      }
    }
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [sharepointStatus, setSharepointStatus] =
    useState<SharePointGraphProvisionStatus>(getInitStatus());

  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | MultiAppsAuthenticationInfoRes>(null);

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (
      [
        SharePointGraphProvisionStatus.NotProvision,
        SharePointGraphProvisionStatus.ProvisioningGrantStep1Done,
      ].includes(sharepointStatus)
    ) {
      ProvisionApi.apiGetMultiAppsAuthenticationInfos(Service.SharePoint, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (
            errorCode === RejectProvisionReason.ServiceConflict ||
            errorCode === RejectProvisionReason.PocConflict
          ) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={SERVICE}
        reason={rejectReason}
      />
    );
  }

  if (
    [
      SharePointGraphProvisionStatus.NotProvision,
      SharePointGraphProvisionStatus.ProvisioningGrantStep1Done,
    ].includes(sharepointStatus) &&
    !authenticationInfo
  ) {
    return <></>;
  }

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('provision.service.sharePoint.title')}</ModalHeader>

          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <GrantPermissionSteps
              {...{
                sharepointStatus,
                setSharepointStatus,
                authenticationInfo,
                orgId,
                setErrorMsg,
              }}
            />
            {sharepointStatus ===
              SharePointGraphProvisionStatus.ProvisioningGrantStep2Done &&
              (syncDataFailFlag ? (
                <DataSyncFailed
                  service={SERVICE}
                  onRetry={async () => {
                    const res = await ProvisionApi.apiRetrySyncData(
                      SERVICE,
                      orgId
                    );
                    if (res.data?.status === 'FAILURE') {
                      if ([813, 814].includes(res.data?.error)) {
                        setErrorMsg(
                          <Trans
                            i18nKey={`provision:provision.service.sharePoint.error.saveInfo.FAILURE;${res.data?.error}`}
                          >
                            placeholder
                            <Link
                              href={getCasOLHLink(
                                CAS_DOC_LINK.provision.sharePoint
                                  .userConfiguration,
                                i18next.language
                              )}
                              target="_blank"
                            />
                            placeholder
                          </Trans>
                        );
                      } else {
                        setErrorMsg(
                          t([
                            `provision.service.sharePoint.error.saveInfo.${res.data?.reason}`,
                            'common.apiException.internalError',
                          ])
                        );
                      }
                    } else {
                      setErrorMsg(undefined);
                      setSyncDataFailFlag(false);
                    }
                  }}
                />
              ) : (
                <DataSyncing
                  onRefresh={async () => {
                    const res = await ProvisionApi.apiGetProvisionStatus(
                      SERVICE,
                      orgId
                    );
                    if (res.data.status === ProvisionStatusEnum.Provisioned) {
                      setSharepointStatus(
                        SharePointGraphProvisionStatus.Provisioned
                      );
                    }
                  }}
                />
              ))}
            {sharepointStatus ===
              SharePointGraphProvisionStatus.Provisioned && <DataSynced />}
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SharePointGraphModal;

const GrantPermissionSteps = ({
  sharepointStatus,
  setSharepointStatus,
  authenticationInfo,
  orgId,
  setErrorMsg,
}: {
  sharepointStatus: SharePointGraphProvisionStatus;
  setSharepointStatus: (status: SharePointGraphProvisionStatus) => void;
  authenticationInfo: MultiAppsAuthenticationInfoRes | null;
  orgId: string;
  setErrorMsg: (msg: string | undefined) => void;
}) => {
  const { t } = useTranslation('provision');
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });

  const onFirstPermission = () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: Service.SharePoint,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const sharepointProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_sp'
      );
      if (sharepointProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(sharepointProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.SharePoint,
              code,
              radioGroupRef.current.enabledPolicyType,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.sharePoint.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              const statusRes = await ProvisionApi.apiGetProvisionStatus(
                Service.SharePoint,
                orgId
              );
              const sharepointTokenStatus =
                statusRes.data?.authorization?.apps?.[0]?.status;
              if (sharepointTokenStatus === TokenStatus.Created) {
                setSharepointStatus(
                  SharePointGraphProvisionStatus.ProvisioningGrantStep1Done
                );
              } else {
                setErrorMsg(
                  t('provision.service.sharePoint.error.saveInfo.FAILURE;450')
                );
              }
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.sharePoint.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  const onSecondPermission = () => {
    const authUrl = authenticationInfo?.[1]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: App.SharePointBasic,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const sharepointBasicProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_basic_sp'
      );
      if (sharepointBasicProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(sharepointBasicProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.SharePointBasic,
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.sharePoint.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setSharepointStatus(
                SharePointGraphProvisionStatus.ProvisioningGrantStep2Done
              );
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.sharePoint.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Steps
        direction="vertical"
        current={sharepointStatus - 1}
        items={[
          {
            title: (
              <Text {...titleTextStyle}>
                <Trans
                  i18nKey={
                    'provision.service.sharePoint.details.userConfiguration.STEP1'
                  }
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.sharePoint.userConfiguration,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                {sharepointStatus <
                SharePointGraphProvisionStatus.ProvisioningGrantStep1Done ? (
                  <>
                    <Divider variant="dashed" orientation="horizontal" />
                    <Text color={colorStyle.color.secondary}>
                      {t('provision.common.selectPolicyToEnable')}
                    </Text>
                    <EnablePolicyRadioGroup
                      service={Service.SharePoint}
                      ref={radioGroupRef}
                    />
                    <Divider variant="dashed" orientation="horizontal" />
                    <Box>
                      <Button
                        variant="secondary"
                        borderRadius="2rem"
                        onClick={() => {
                          onFirstPermission();
                        }}
                      >
                        {t('provision.button.grantPermission')}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  </Box>
                )}
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  sharepointStatus >=
                  SharePointGraphProvisionStatus.ProvisioningGrantStep1Done
                }
              />
            ),
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t(
                  'provision.service.sharePoint.details.userConfiguration.STEP2'
                )}
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                <Box>
                  {sharepointStatus <
                  SharePointGraphProvisionStatus.ProvisioningGrantStep2Done ? (
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      disabled={
                        sharepointStatus !==
                        SharePointGraphProvisionStatus.ProvisioningGrantStep1Done
                      }
                      onClick={() => {
                        onSecondPermission();
                      }}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  ) : (
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  )}
                </Box>
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  sharepointStatus >=
                  SharePointGraphProvisionStatus.ProvisioningGrantStep2Done
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};
